// src/modules/competitions/components/VideoComparator.tsx

import React, { useRef, useState, useEffect } from 'react';
import '../styles/videoComparator.css';

interface Props {
    exampleVideo: string;
    participantVideo: string;
    exampleTitle?: string;
    isLoading?: boolean;
}

const VideoComparator: React.FC<Props> = ({
    exampleVideo,
    participantVideo,
    exampleTitle,
    isLoading = false
}) => {
    const exampleRef = useRef<HTMLVideoElement>(null);
    const participantRef = useRef<HTMLVideoElement>(null);
    
    const [examplePlaying, setExamplePlaying] = useState(false);
    const [participantPlaying, setParticipantPlaying] = useState(false);
    const [exampleError, setExampleError] = useState(false);
    const [participantError, setParticipantError] = useState(false);

    // Reiniciar errores cuando cambien las URLs
    useEffect(() => {
        setExampleError(false);
        setParticipantError(false);
    }, [exampleVideo, participantVideo]);

    const handleExamplePlayPause = () => {
        if (exampleRef.current) {
            if (examplePlaying) {
                exampleRef.current.pause();
            } else {
                exampleRef.current.play();
            }
            setExamplePlaying(!examplePlaying);
        }
    };

    const handleParticipantPlayPause = () => {
        if (participantRef.current) {
            if (participantPlaying) {
                participantRef.current.pause();
            } else {
                participantRef.current.play();
            }
            setParticipantPlaying(!participantPlaying);
        }
    };

    const handleExampleEnded = () => {
        setExamplePlaying(false);
    };

    const handleParticipantEnded = () => {
        setParticipantPlaying(false);
    };

    const handleExampleError = (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
        console.error('Error cargando video ejemplo:', e);
        setExampleError(true);
    };

    const handleParticipantError = (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
        console.error('Error cargando video participante:', e);
        setParticipantError(true);
    };

    if (isLoading) {
        return (
            <div className="video-comparator">
                <div className="video-loading">
                    <p>⏳ Cargando videos...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="video-comparator">
            {/* Video Ejemplo */}
            <div className="video-container">
                <div className="video-label example-label">EJEMPLO</div>
                {exampleTitle && (
                    <div className="video-title">{exampleTitle}</div>
                )}
                <div className="video-wrapper">
                    {exampleError ? (
                        <div className="video-error">
                            <p>❌ Error al cargar video ejemplo</p>
                            <p className="error-detail">El video no está disponible</p>
                        </div>
                    ) : (
                        <video
                            ref={exampleRef}
                            src={exampleVideo}
                            className="comparison-video"
                            onEnded={handleExampleEnded}
                            onError={handleExampleError}
                            controls
                        />
                    )}
                </div>
                {!exampleError && (
                    <div className="video-controls">
                        <button 
                            className="play-button"
                            onClick={handleExamplePlayPause}
                        >
                            {examplePlaying ? '⏸️ Pausar' : '▶️ Reproducir'}
                        </button>
                    </div>
                )}
            </div>

            {/* Divisor */}
            <div className="comparator-divider">
                <div className="divider-line" />
                <div className="vs-badge">VS</div>
                <div className="divider-line" />
            </div>

            {/* Video Participante */}
            <div className="video-container">
                <div className="video-label participant-label">TU VIDEO</div>
                <div className="video-wrapper">
                    {participantError ? (
                        <div className="video-error">
                            <p>❌ Error al cargar tu video</p>
                            <p className="error-detail">El video no está disponible</p>
                        </div>
                    ) : (
                        <video
                            ref={participantRef}
                            src={participantVideo}
                            className="comparison-video"
                            onEnded={handleParticipantEnded}
                            onError={handleParticipantError}
                            controls
                        />
                    )}
                </div>
                {!participantError && (
                    <div className="video-controls">
                        <button 
                            className="play-button"
                            onClick={handleParticipantPlayPause}
                        >
                            {participantPlaying ? '⏸️ Pausar' : '▶️ Reproducir'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default VideoComparator;